import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "./icons";
import { cx } from "./ui";

/**
 * A compact copy control for a value that is already on screen — an address,
 * a hostname — where `CopyField` would put the same string in a box beside
 * itself and say it twice.
 */
export function CopyButton({
  value,
  label,
  className,
  children,
}: {
  value: string;
  /** Announced to a screen reader, e.g. "Copy the connection host". */
  label: string;
  className?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation("common");
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context or denied). Saying nothing is
      // better than claiming a copy that did not happen.
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      aria-label={label}
      className={cx(
        "inline-flex min-h-7 min-w-7 shrink-0 items-center justify-center gap-1.5 text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg",
        className,
      )}
    >
      <Icon name={copied ? "check" : "copy"} className="size-3.5" />
      {children}
      {/* Announced without being drawn: the icon swap is the visual feedback,
          and a screen reader gets nothing from it. */}
      <span className="sr-only" role="status">
        {copied ? t("copy.copied") : ""}
      </span>
    </button>
  );
}
